import './CrossProductsPage.scss'

import { DownCircleFilled, LeftOutlined, MinusOutlined, PlusOutlined, RightOutlined, ShoppingCartOutlined } from '@ant-design/icons'
import { useApolloClient } from '@apollo/client'
import { Button, Card, Col, message, Modal, notification, Pagination, Row, Select } from 'antd'
import Spinner from 'components/spinner/Spinner'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import CenteredLayout from '../../components/centered-layout/CenteredLayout'
import CrossProductFilterPage from '../../components/cross-products/crossProductFilter'
import AppContext from '../../context/AppContext'
import {
  CaprazUrunFiltre,
  KategoriveMarkalarDocument,
  SepetDocument,
  useCarprazUrunlerQuery,
  useGetCategoriesQuery,
  useSepetQuery,
  useSepetUrunDuzenleMutation,
  useSepetUrunEkleMutation,
  useSepetUrunSilMutation,
} from '../../gql/GeneratedGraphqlTypes'
import { LISTE_DISI_URUNLER_KATEGORI_ID } from '../../utils/const'
// import { ReactComponent as FilterIcon } from './images/filter.svg'
import { ReactComponent as Ribbon } from './images/Ribbon.svg'

export type SelectedCategory = {
  id: number
  isim: string
}

const ProductImageCarousel: React.FC<{ images: string[] }> = ({ images }) => {
  const [current, setCurrent] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const filtered = images.filter(Boolean)

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrent((c) => (c - 1 + filtered.length) % filtered.length)
  }
  const next = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrent((c) => (c + 1) % filtered.length)
  }

  return (
    <>
      <div
        className="carousel-wrapper"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setModalOpen(true)}
        style={{ cursor: 'zoom-in' }}
      >
        <div className="carousel-track" style={{ transform: `translateX(-${current * 100}%)` }}>
          {filtered.map((src, i) => (
            <img key={i} src={src} alt="" className="carousel-img" />
          ))}
        </div>

        {filtered.length > 1 && (
          <>
            <button className={`carousel-btn carousel-btn--left ${hovered ? 'visible' : ''}`} onClick={prev}>
              <LeftOutlined />
            </button>
            <button className={`carousel-btn carousel-btn--right ${hovered ? 'visible' : ''}`} onClick={next}>
              <RightOutlined />
            </button>
            <div className="carousel-dots">
              {filtered.map((_, i) => (
                <span
                  key={i}
                  className={`carousel-dot ${i === current ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setCurrent(i) }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onCancel={(e) => { e.stopPropagation(); setModalOpen(false) }}
        footer={null}
        centered
        width={600}
        className="carousel-modal"
        destroyOnClose
      >
        <div className="carousel-modal-inner">
          <div className="carousel-modal-track" style={{ transform: `translateX(-${current * 100}%)` }}>
            {filtered.map((src, i) => (
              <img key={i} src={src} alt="" className="carousel-modal-img" />
            ))}
          </div>

          {filtered.length > 1 && (
            <>
              <button className="carousel-btn carousel-btn--left visible carousel-btn--modal" onClick={prev}>
                <LeftOutlined />
              </button>
              <button className="carousel-btn carousel-btn--right visible carousel-btn--modal" onClick={next}>
                <RightOutlined />
              </button>
              <div className="carousel-dots carousel-dots--modal">
                {filtered.map((_, i) => (
                  <span
                    key={i}
                    className={`carousel-dot ${i === current ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setCurrent(i) }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

const CrossProductPage = () => {
  const ustKategoriId = LISTE_DISI_URUNLER_KATEGORI_ID
  const params = useParams()
  const { slug, okul } = useContext(AppContext)
  const navigate = useNavigate()
  const apolloClient = useApolloClient()
  const [messageApi, contextHolder] = message.useMessage()
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])
  const [selectedMarkaIds, setSelectedMarkaIds] = useState<number[]>([])
  const [sepetUrunEkleMutation, { loading }] = useSepetUrunEkleMutation({ refetchQueries: [SepetDocument] })
  const [sepetUrunDuzenleMutation] = useSepetUrunDuzenleMutation({ refetchQueries: [SepetDocument] })
  const [sepetUrunSilMutation] = useSepetUrunSilMutation({ refetchQueries: [SepetDocument] })
  const [cartQuantities, setCartQuantities] = useState<Record<number, number>>({})
  const [currentPage, setCurrentPage] = useState(1) // for pagination

  useEffect(() => {
    if (okul?.active === false) {
      navigate('/')
    }
  }, [okul?.active, navigate])

  useSepetQuery({
    variables: { slug: slug! },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const quantities: Record<number, number> = {}
      data?.sepetTest?.urunler?.forEach((item) => {
        if (item?.urun?.id) {
          quantities[item.urun.id] = item.adet
        }
      })
      setCartQuantities(quantities)
    },
  })

  const [selectedOption, setSelectedOption] = useState(1)
  const [minPrice, setMinPrice] = useState<number | null>(null)
  const [maxPrice, setMaxPrice] = useState<number | null>(null)
  const [minTempPrice, setMinTempPrice] = useState(0)
  const [maxTempPrice, setMaxTempPrice] = useState(10000)
  const [absoluteMin, setAbsoluteMin] = useState(0)
  const [absoluteMax, setAbsoluteMax] = useState(10000)
  const [sortedData, setSortedData] = useState<any[]>([]) // Declare sortedData state variable

  let queryVariables = {
    slug: slug!,
    filtre: {} as CaprazUrunFiltre,
  }

  useEffect(() => {
    if (okul?.capraz_urun_satisi_varmi === false) {
      navigate(`/`)
    }

    const paramId = parseInt(params.kategoriId!)
    if (!isNaN(paramId) && paramId !== ustKategoriId) setSelectedCategoryIds([paramId])
  }, [])

  const handleCategoryChange = (newIds: number[]) => {
    setSelectedCategoryIds(newIds)
    setSelectedMarkaIds([])
    setMinPrice(null)
    setMaxPrice(null)
    setCurrentPage(1)
  }

  const handleMarkaChange = (newIds: number[]) => {
    setSelectedMarkaIds(newIds)
    setCurrentPage(1)
  }

  queryVariables.filtre = {
    marka: selectedMarkaIds,
    min_fiyat: minPrice,
    max_fiyat: maxPrice,
    kategori: selectedCategoryIds.length > 0 ? selectedCategoryIds : [ustKategoriId],
    sayfa: currentPage - 1,
  }

  const { data, loading: caprazUrunLoading } = useCarprazUrunlerQuery({
    variables: queryVariables,
  })

  // Fiyat aralığını hesaplamak için sadece kategoriye göre çek (marka/fiyat filtresi yok)
  const { data: allProductsData } = useCarprazUrunlerQuery({
    variables: {
      slug: slug!,
      filtre: {
        kategori: selectedCategoryIds.length > 0 ? selectedCategoryIds : [ustKategoriId],
        sayfa: 0,
      },
    },
  })

  const { data: categories, loading: categoryLoading } = useGetCategoriesQuery({
    variables: {
      ust_kategori_id: ustKategoriId, // Ust kategori id
    },
  })

  const [mergedMarkalar, setMergedMarkalar] = useState<{ id: number; isim: string }[]>([])

  useEffect(() => {
    const kategoriIds = selectedCategoryIds.length > 0 ? selectedCategoryIds : [ustKategoriId]
    const fetchBrands = async () => {
      const brandMap = new Map<number, { id: number; isim: string }>()
      await Promise.all(
        kategoriIds.map(async (katId) => {
          const result = await apolloClient.query({
            query: KategoriveMarkalarDocument,
            variables: { kategoriId: katId },
          })
          result.data?.urunKategorisi?.markalar?.forEach((m: { id: number; isim: string }) => {
            brandMap.set(m.id, m)
          })
        }),
      )
      setMergedMarkalar(Array.from(brandMap.values()))
    }
    fetchBrands()
  }, [selectedCategoryIds])

  useEffect(() => {
    const products = allProductsData?.okul?.urunler?.capraz_urunler
    if (!products || products.length === 0) return
    const prices = products
      .map((p) => parseFloat(p?.indirimli_birim_fiyat ?? p?.indirimsiz_birim_fiyat ?? '0'))
      .filter((p) => p > 0)
    if (prices.length === 0) return
    const newMin = Math.floor(Math.min(...prices))
    const newMax = Math.ceil(Math.max(...prices))
    setAbsoluteMin(newMin)
    setAbsoluteMax(newMax)
    setMinTempPrice(newMin)
    setMaxTempPrice(newMax)
  }, [allProductsData])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data?.okul?.urunler?.capraz_urunler) return
    const fresh = [...data.okul.urunler.capraz_urunler]
    switch (selectedOption) {
      case 2:
        fresh.sort((a, b) => parseFloat(a?.indirimli_birim_fiyat ?? '0') - parseFloat(b?.indirimli_birim_fiyat ?? '0'))
        break
      case 3:
        fresh.sort((a, b) => parseFloat(b?.indirimli_birim_fiyat ?? '0') - parseFloat(a?.indirimli_birim_fiyat ?? '0'))
        break
      case 4:
        fresh.sort((a, b) => (a?.urun?.isim ?? '').localeCompare(b?.urun?.isim ?? ''))
        break
      case 5:
        fresh.sort((a, b) => (b?.urun?.isim ?? '').localeCompare(a?.urun?.isim ?? ''))
        break
    }
    setSortedData(fresh)
  }, [data])

  const isMounted = useRef(false)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentPage, selectedCategoryIds, selectedMarkaIds, minPrice, maxPrice])

  const handleClick = (index: number) => {
    const targetUrl = `/urun/${index}`
    navigate(targetUrl)
  }

  const handleAddToCart = async (productId: number) => {
    try {
      await sepetUrunEkleMutation({
        variables: {
          okulSlug: slug!,
          urun: { urun_id: productId!, adet: 1 },
        },
      })
      setCartQuantities((prev) => ({ ...prev, [productId]: 1 }))
      notification.open({
        message: 'Başarılı',
        description: 'Ürün başarılı bir şekilde sepete eklendi.',
        type: 'success',
      })
    } catch (error) {
      messageApi.error(`${error}`, 6)
    }
  }

  const handleIncrement = async (productId: number) => {
    const newQty = (cartQuantities[productId] ?? 0) + 1
    try {
      await sepetUrunDuzenleMutation({
        variables: {
          okulSlug: slug!,
          urun: { urun_id: productId, adet: newQty, ogrenci_ismi: '' },
        },
      })
      setCartQuantities((prev) => ({ ...prev, [productId]: newQty }))
      notification.open({
        message: 'Güncellendi',
        description: `Ürün adedi ${newQty} olarak güncellendi.`,
        type: 'success',
      })
    } catch (error) {
      notification.open({
        message: 'Hata',
        description: 'Adet güncellenirken bir hata oluştu.',
        type: 'error',
      })
    }
  }

  const handleDecrement = async (productId: number) => {
    const currentQty = cartQuantities[productId] ?? 0
    if (currentQty <= 1) {
      try {
        await sepetUrunSilMutation({
          variables: {
            okulSlug: slug!,
            urunId: productId,
          },
        })
        setCartQuantities((prev) => {
          const updated = { ...prev }
          delete updated[productId]
          return updated
        })
        notification.open({
          message: 'Ürün Kaldırıldı',
          description: 'Ürün sepetten çıkarıldı.',
          type: 'info',
        })
      } catch (error) {
        notification.open({
          message: 'Hata',
          description: 'Ürün sepetten kaldırılırken bir hata oluştu.',
          type: 'error',
        })
      }
    } else {
      const newQty = currentQty - 1
      try {
        await sepetUrunDuzenleMutation({
          variables: {
            okulSlug: slug!,
            urun: { urun_id: productId, adet: newQty, ogrenci_ismi: '' },
          },
        })
        setCartQuantities((prev) => ({ ...prev, [productId]: newQty }))
        notification.open({
          message: 'Güncellendi',
          description: `Ürün adedi ${newQty} olarak güncellendi.`,
          type: 'success',
        })
      } catch (error) {
        notification.open({
          message: 'Hata',
          description: 'Adet güncellenirken bir hata oluştu.',
          type: 'error',
        })
      }
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleFilterSelectChange = (value: number) => {
    setSelectedOption(value)
    if (value !== 1) {
      let updatedSortedData = [...sortedData]

      switch (value) {
        case 2:
          updatedSortedData = updatedSortedData.sort(
            (a, b) => parseFloat(a?.indirimli_birim_fiyat ?? '0') - parseFloat(b?.indirimli_birim_fiyat ?? '0'),
          )
          break
        case 3:
          updatedSortedData = updatedSortedData.sort(
            (a, b) => parseFloat(b?.indirimli_birim_fiyat ?? '0') - parseFloat(a?.indirimli_birim_fiyat ?? '0'),
          )
          break
        case 4:
          updatedSortedData = updatedSortedData.sort((a, b) => (a?.urun?.isim ?? '').localeCompare(b?.urun?.isim ?? ''))
          break
        case 5:
          updatedSortedData = updatedSortedData.sort((a, b) => (b?.urun?.isim ?? '').localeCompare(a?.urun?.isim ?? ''))
          break
        default:
          break
      }

      setSortedData(updatedSortedData)
      setCurrentPage(1)
    } else {
      setSortedData([...(data?.okul?.urunler?.capraz_urunler ?? [])])
      setCurrentPage(1)
    }
  }

  if (loading || caprazUrunLoading || false || categoryLoading) {
    return (
      <Spinner showSpinner={loading || caprazUrunLoading || false || categoryLoading} color="red" />
    )
  }

  return (
    <div className="cross-product-container">
      <CenteredLayout>
        {contextHolder}
        <div className="cross-product-header">
          <span> Diğer Ürünler</span>
          <Ribbon className="ribbon" />
        </div>
        <Row gutter={[24, 24]}>
          <Col style={{ padding: 0 }} span={24} order={2} lg={{ span: 18, push: 6, order: 1 }}>
            <div className="sort-bar">
              <h1 style={{ margin: 0 }}>
                {selectedCategoryIds.length === 1
                  ? categories?.urunKategorileri?.find((cat) => cat?.id === selectedCategoryIds[0])?.isim
                  : selectedCategoryIds.length > 1
                  ? selectedCategoryIds
                      .map((id) => categories?.urunKategorileri?.find((cat) => cat?.id === id)?.isim)
                      .filter(Boolean)
                      .join(', ')
                  : null}
              </h1>
              <div className="sort-bar-right">
                <Select
                  bordered={false}
                  value={selectedOption}
                  suffixIcon={<DownCircleFilled />}
                  onSelect={(value) => handleFilterSelectChange(Number(value))}
                  options={[
                    { value: 1, label: 'Önerilen' },
                    { value: 2, label: 'Fiyata Göre Artan' },
                    { value: 3, label: 'Fiyata Göre Azalan' },
                    { value: 4, label: "A'dan Z'ye" },
                    { value: 5, label: "Z'den A'ya" },
                  ]}
                />
                <span className="result-count">
                  {(data?.okul?.urunler?.urun_sayisi ?? 0) > 0
                    ? `${data!.okul!.urunler!.urun_sayisi} sonuç bulundu`
                    : 'Sonuç bulunamadı'}
                </span>
              </div>
            </div>
            <>
              <div className="cross-product-card-container">
                {sortedData.length > 0 &&
                  sortedData.map((product, index) => {
                    const resimler: string[] = product?.urun?.resimler ?? []
                    return (
                      <div className="card-item" key={`cross_urun_${index}_${product?.urun?.id}`}>
                        <ProductImageCarousel images={resimler} />
                        <h3 className="card-prod-name" onClick={() => handleClick(product?.urun?.id ?? 0)}>
                          {product?.urun?.isim}
                        </h3>
                        <div className="card-footer">
                          <div className="card-description">
                            {product?.indirimsiz_birim_fiyat &&
                            product.indirimsiz_birim_fiyat > product?.indirimli_birim_fiyat! ? (
                              <span className="card-indirimsiz-fiyat">{product?.indirimli_birim_fiyat} TL</span>
                            ) : null}
                            <span className="card-indirimli-fiyat">{product?.indirimsiz_birim_fiyat} TL</span>
                          </div>
                          <div className="card-btn-container">
                            {cartQuantities[product?.urun?.id ?? 0] ? (
                              <div className="card-qty-selector">
                                <Button
                                  className="qty-btn"
                                  icon={<MinusOutlined />}
                                  onClick={() => handleDecrement(product?.urun?.id ?? 0)}
                                />
                                <span className="qty-value">{cartQuantities[product?.urun?.id ?? 0]}</span>
                                <Button
                                  className="qty-btn"
                                  icon={<PlusOutlined />}
                                  onClick={() => handleIncrement(product?.urun?.id ?? 0)}
                                />
                              </div>
                            ) : (
                              <Button
                                className="card-sepete-ekle-btn"
                                onClick={() => handleAddToCart(product?.urun?.id ?? 0)}
                              >
                                <ShoppingCartOutlined />
                                Sepete Ekle
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>

              {sortedData.length <= 0 && (
                <Col span={24}>
                  <Card className="urun-card" style={{ textAlign: 'center' }}>
                    <p>Filtre sonucu ürün bulunamadı.</p>
                  </Card>
                </Col>
              )}
            </>
            <Pagination
              current={currentPage}
              pageSize={20}
              total={data?.okul?.urunler?.urun_sayisi}
              onChange={handlePageChange}
              style={{ marginBottom: 16, marginTop: 16, textAlign: 'center' }}
              showSizeChanger={false}
            />
          </Col>
          <Col span={24} order={1} lg={{ span: 6, pull: 18, order: 2 }}>
            <CrossProductFilterPage
              categories={categories?.urunKategorileri}
              selectedCategoryIds={selectedCategoryIds}
              onCategoryChange={handleCategoryChange}
              markalar={mergedMarkalar}
              selectedMarkaIds={selectedMarkaIds}
              onMarkaChange={handleMarkaChange}
              minPrice={minPrice}
              maxPrice={maxPrice}
              setMinPrice={setMinPrice}
              setMaxPrice={setMaxPrice}
              minTempPrice={minTempPrice}
              setMinTempPrice={setMinTempPrice}
              maxTempPrice={maxTempPrice}
              setMaxTempPrice={setMaxTempPrice}
              absoluteMin={absoluteMin}
              absoluteMax={absoluteMax}
            />
          </Col>
        </Row>
      </CenteredLayout>
    </div>
  )
}

export default CrossProductPage
